import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@material-ui/core';
import { Alert, Button } from 'components';

const ConfirmationModal = ({
  open,
  onClose,
  cancelLabel,
  title,
  onConfirm,
  confirmLabel,
  description,
  type,
  reason,
  size
}) => {
  const [value, setValue] = useState('');
  const [reasonValue, setReasonValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [confirmationMessage, setConfirmationMessage] = useState(null);

  const invalid = useMemo(() => (
    (!!type && type !== value) ||
    (reason && reasonValue.length < 5)
  ), [reason, reasonValue.length, type, value]);

  const handleConfirm = useCallback(async evt => {
    setErrorMessage(null);
    setIsLoading(true);
    try {
      const response = await onConfirm(reasonValue);
      if (response) {
        setConfirmationMessage(response);
      } else {
        onClose(evt);
      }
    } catch (err) {
      setErrorMessage(err?.message);
    }
    setIsLoading(false);
  }, [onClose, onConfirm, reasonValue]);

  useEffect(() => {
    if (open) {
      setConfirmationMessage(null);
      setValue('');
      setReasonValue('');
      setErrorMessage(null);
      setIsLoading(false);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={!!size}
      maxWidth={size}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
    >
      {!!title && (
        <DialogTitle id="dialog-title">{title}</DialogTitle>
      )}
      <DialogContent>
        {!!confirmationMessage && (
          <DialogContentText>
            {confirmationMessage}
          </DialogContentText>
        )}
        {!!description && !confirmationMessage && (
          <DialogContentText id="dialog-description">
            {description}
          </DialogContentText>
        )}
        {!!type && !confirmationMessage && (
          <TextField
            value={value}
            onChange={e => setValue(e.target.value)}
            autoFocus
            fullWidth
            id="confirm"
            autoComplete="off"
            disabled={isLoading}
            placeholder={`para confirmar digite '${type}' sem aspas`}
          />
        )}
        {!!reason && !confirmationMessage && (
          <TextField
            value={reasonValue}
            onChange={e => setReasonValue(e.target.value)}
            autoFocus={!type}
            fullWidth
            id="reason"
            multiline
            rows={3}
            disabled={isLoading}
            placeholder="Informe um motivo"
          />
        )}
        {!!errorMessage && (
          <Alert severity="error" mt={2}>{errorMessage}</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={isLoading}
          color={confirmationMessage ? 'secondary' : 'default'}
          variant={confirmationMessage ? 'contained' : 'text'}
        >
          {confirmationMessage ? 'Ok' : cancelLabel}
        </Button>
        {!!onConfirm && !confirmationMessage && (
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="secondary"
            autoFocus={!type}
            disabled={invalid}
            loading={isLoading}
          >
            {confirmLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

ConfirmationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  cancelLabel: PropTypes.string.isRequired,
  title: PropTypes.string,
  onConfirm: PropTypes.func,
  confirmLabel: PropTypes.string,
  description: PropTypes.node,
  type: PropTypes.string,
  reason: PropTypes.bool,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl'])
};

ConfirmationModal.defaultProps = {
  confirmLabel: 'Confirmar',
  cancelLabel: 'Cancelar',
  size: 'xs'
};

export default ConfirmationModal;
